import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation, Trans } from 'react-i18next';
import { Mail, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setSent(true);
    } catch (_) {
      // Generic response by design — still show the confirmation.
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-xl" data-testid="forgot-password-page">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="w-4 h-4" /> {t('auth.backToLogin', 'Tilbake til innlogging')}
        </Link>

        {sent ? (
          <div className="text-center space-y-3" data-testid="forgot-password-sent">
            <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto" />
            <h1 className="text-xl font-bold">{t('forgotPassword.checkEmail', 'Sjekk e-posten din')}</h1>
            <p className="text-sm text-muted-foreground">
              <Trans i18nKey="forgotPassword.sentDesc" values={{ email }} defaults="Hvis <1>{{email}}</1> finnes hos oss, har vi sendt en lenke for å tilbakestille passordet. Lenken er gyldig i 1 time."
                components={{ 1: <span className="font-medium text-foreground" /> }} />
            </p>
            <Button asChild className="w-full mt-2"><Link to="/login">{t('auth.toLogin', 'Til innlogging')}</Link></Button>
          </div>
        ) : (
          <>
            <div className="mb-6">
              <h1 className="text-2xl font-bold">{t('forgotPassword.title', 'Glemt passord?')}</h1>
              <p className="text-sm text-muted-foreground mt-1">{t('forgotPassword.desc', 'Skriv inn e-postadressen din, så sender vi deg en tilbakestillingslenke.')}</p>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('auth.emailPlaceholder', 'navn@firma.no')}
                  className="pl-10 h-12"
                  data-testid="forgot-email-input"
                />
              </div>
              <Button type="submit" disabled={loading} className="w-full h-12 font-semibold" data-testid="forgot-submit-btn">
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('forgotPassword.submit', 'Send tilbakestillingslenke')}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
